import React, { useMemo } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import {
  Heart, X as XIcon, Loader2, AlertTriangle, Mic, ImageIcon, Sparkles,
  Video as VideoIcon,
} from 'lucide-react';

/**
 * A single like/dislike swipe card backed by framer-motion.
 *
 * The card itself is a draggable vertical 9:16 panel. Horizontal drag past
 * a threshold (~35% of card width) commits to like/dislike. Buttons are
 * always visible for keyboard + accessibility; drag is additive.
 *
 * Props
 *   sample      { sample_id, template_id, template_name, status, video_url,
 *                 demo_video_fallback, has_voiceover, media_count, error? }
 *   position    'top' | 'queue'  . Top card is interactive, queue cards
 *                                   render stacked behind it.
 *   depth       integer offset from the top card (0 = top). Affects scale
 *                                   and y-translation for the stacked look.
 *   onSwipe     (verdict) => void. Called once when the card is flung off
 *                                   or a button is clicked.
 */
export default function SwipeCard({ sample, position = 'top', depth = 0, onSwipe }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-18, 0, 18]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const dislikeOpacity = useTransform(x, [-140, -40], [1, 0]);

  const isTop = position === 'top';

  // Stack effect: cards behind the top one shrink + drop slightly.
  const stackStyle = useMemo(
    () => ({
      scale: 1 - Math.min(depth, 2) * 0.04,
      y: Math.min(depth, 2) * 10,
    }),
    [depth]
  );

  const handleDragEnd = (_e, info) => {
    if (!isTop || typeof onSwipe !== 'function') return;
    const threshold = 120;
    if (info.offset.x > threshold) {
      onSwipe('like');
    } else if (info.offset.x < -threshold) {
      onSwipe('dislike');
    }
  };

  const isPending = sample.status === 'pending' || sample.status === 'rendering' || sample.status === 'waiting' || sample.status === 'planned' || sample.status === 'processing';
  const isSucceeded = sample.status === 'succeeded' && !!sample.video_url;
  const isFailed = sample.status === 'failed';
  // Pending samples show the registry's generic demo so the user can still
  // swipe on visual style. The "tailored" version catches up in a moment.
  const videoSrc = isSucceeded ? sample.video_url : (sample.demo_video_fallback || null);

  return (
    <motion.div
      className={`absolute inset-0 rounded-2xl overflow-hidden shadow-2xl border ${
        isTop ? 'border-[#d8d0bd]' : 'border-[#e3dccb] pointer-events-none'
      } bg-[#f7f3ea]`}
      style={{ x: isTop ? x : 0, rotate: isTop ? rotate : 0, ...stackStyle }}
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      whileDrag={{ cursor: 'grabbing' }}
      animate={isTop ? { scale: 1, y: 0 } : stackStyle}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
    >
      {/* Video / poster layer.
          Only the TOP card actually plays its video. Queue cards (depth 1+)
          stay as a calm dark gradient with the template name. That way the
          stacked-behind card never peeks out as a different-sized "preview"
          when the visible-bounds offset shifts. Plus we don't fire 2 extra
          autoplay <video> elements that nobody is looking at. */}
      <div className="absolute inset-0 bg-[#f7f3ea]">
        {isTop && videoSrc ? (
          <video
            key={videoSrc}
            src={videoSrc}
            className={`w-full h-full object-cover ${isPending ? 'grayscale opacity-70' : ''}`}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#fffdf7] via-black/70 to-[#fffdf7]" />
        )}
      </div>

      {/* Top gradient for title readability */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
      {/* Bottom gradient for metadata */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

      {/* Top-left: template name */}
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between text-white">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-white/60">Template</p>
          <p className="text-lg font-semibold truncate drop-shadow">{sample.template_name}</p>
        </div>
        {isPending && (
          <div className="flex items-center gap-1.5 bg-black/50 backdrop-blur px-2 py-1 rounded-full text-xs text-white/80">
            <Sparkles className="w-3 h-3" />
            Rendert…
          </div>
        )}
      </div>

      {/* Bottom-left: metadata chips. Label + icon switch on the template's
          media_type so video templates (Video Montage / Swipe Up) read
          "X Videos" with a film icon, not "X Fotos". */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-xs text-white/80">
        <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur px-2 py-1 rounded-full">
          {sample.media_type === 'video' ? (
            <VideoIcon className="w-3 h-3" />
          ) : (
            <ImageIcon className="w-3 h-3" />
          )}
          {sample.media_count || '?'} {sample.media_type === 'video' ? 'Videos' : 'Fotos'}
        </span>
        {sample.has_voiceover && (
          <span className="inline-flex items-center gap-1 bg-[#1f4d3a]/30 backdrop-blur px-2 py-1 rounded-full text-[#2e6b50]">
            <Mic className="w-3 h-3" />
            Voiceover
          </span>
        )}
        {isPending && (
          <span className="inline-flex items-center gap-1 text-white/60">
            <Loader2 className="w-3 h-3 animate-spin" />
            Dein Render kommt gleich
          </span>
        )}
      </div>

      {/* Swipe overlays */}
      {isTop && (
        <>
          <motion.div
            style={{ opacity: likeOpacity }}
            className="absolute top-8 right-8 border-4 border-[#1e7a4f] text-[#1e7a4f] font-bold text-3xl px-4 py-2 rounded-xl rotate-12 backdrop-blur pointer-events-none"
          >
            LIKE
          </motion.div>
          <motion.div
            style={{ opacity: dislikeOpacity }}
            className="absolute top-8 left-8 border-4 border-[#b3543f] text-[#b3543f] font-bold text-3xl px-4 py-2 rounded-xl -rotate-12 backdrop-blur pointer-events-none"
          >
            NOPE
          </motion.div>
        </>
      )}

      {/* Failed state overlay */}
      {isFailed && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur flex flex-col items-center justify-center gap-3 text-center px-6">
          <AlertTriangle className="w-10 h-10 text-[#9a6d1f]" />
          <p className="text-sm text-[#23211a] font-medium">Render fehlgeschlagen</p>
          <p className="text-xs text-[#6f6a5c] max-w-xs">
            Du kannst diese Vorlage einfach überspringen oder später erneut versuchen.
          </p>
        </div>
      )}

      {/* Like / Dislike buttons at the bottom. Always visible */}
      {isTop && typeof onSwipe === 'function' && (
        <div className="absolute -bottom-20 inset-x-0 flex items-center justify-center gap-8 pointer-events-none">
          {/* Buttons actually sit OUTSIDE the card visually via absolute offset
              so the card stays clean; pointer-events re-enabled per button. */}
        </div>
      )}
    </motion.div>
  );
}

/**
 * External action bar. Rendered outside the card in SampleSwipeStep so the
 * bottom of the card can still show metadata without overlap. Exported as
 * a sibling so layout stays compositional.
 */
export function SwipeActionBar({ onDislike, onLike, onUndo, canUndo }) {
  return (
    <div className="flex items-center justify-center gap-6">
      <button
        type="button"
        onClick={onDislike}
        className="w-14 h-14 rounded-full border-2 border-[#b3543f]/70 text-[#b3543f] flex items-center justify-center hover:bg-[#f7e8e3] active:scale-95 transition shadow-lg shadow-red-500/10"
        aria-label="Dislike"
      >
        <XIcon className="w-7 h-7" />
      </button>
      {onUndo && (
        <button
          type="button"
          onClick={onUndo}
          disabled={!canUndo}
          className="w-10 h-10 rounded-full border border-[#d8d0bd] text-[#6f6a5c] flex items-center justify-center disabled:opacity-40 hover:text-[#23211a] hover:border-[#1f4d3a]/50 active:scale-95 transition text-xs"
          aria-label="Undo"
        >
          ↶
        </button>
      )}
      <button
        type="button"
        onClick={onLike}
        className="w-14 h-14 rounded-full border-2 border-[#1e7a4f]/70 text-[#1e7a4f] flex items-center justify-center hover:bg-[#e4f0e8] active:scale-95 transition shadow-lg shadow-emerald-500/10"
        aria-label="Like"
      >
        <Heart className="w-7 h-7" />
      </button>
    </div>
  );
}
